import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Card, Flex, Popconfirm, Space, Typography } from 'antd';
import type { InventorizeReviewField } from '@/features/inventorization/domain/review/inventorize-review-fields';
import {
  createRegisterReviewGroups,
  formatRegisterFieldLabel,
  getRegisterFields,
  getRegisterPhotos,
} from '@/features/inventorization/review/register-review-model';
import { reviewRegisterLabel, reviewText } from '@/features/inventorization/review-i18n';
import { PhotoReviewGallery } from '@/routers/inventorization/components/review/PhotoReviewGallery';
import { RegisterPhotoCaptureButton } from '@/routers/inventorization/components/review/RegisterPhotoCaptureButton';
import { RegisterProgress } from '@/routers/inventorization/components/review/RegisterProgress';
import { ReviewFieldList } from '@/routers/inventorization/components/review/ReviewFieldList';
import type { CaptureSession } from '@/shared/capture/capture-session';

export function RegisterReviewGroups({
  fields,
  onAddRegister,
  onApproveField,
  onCaptureRegisterPhoto,
  onOpenField,
  onRemoveRegister,
  session,
}: {
  fields: InventorizeReviewField[];
  onAddRegister: () => Promise<void> | void;
  onApproveField?: (field: InventorizeReviewField) => void;
  onCaptureRegisterPhoto: (registerId: string, file: File) => Promise<void> | void;
  onOpenField: (field: InventorizeReviewField) => void;
  onRemoveRegister: (registerId: string) => Promise<void> | void;
  session: CaptureSession;
}) {
  const registers = createRegisterReviewGroups(session, fields);

  if (registers.length === 0) {
    return (
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Typography.Text type="secondary">
          {reviewText('register.noneAdded', 'No registers added yet.')}
        </Typography.Text>
        <Button icon={<PlusOutlined />} onClick={() => void onAddRegister()}>
          {reviewText('register.add', 'Add register')}
        </Button>
      </Space>
    );
  }

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      {registers.map((register, index) => {
        const registerFields = getRegisterFields(fields, register.id);
        const photos = getRegisterPhotos(
          session.photos.filter(photo => !photo.uploadedRemoteId),
          register,
        );
        const canRemove = registers.length > 1;

        if (registerFields.length === 0 && photos.length === 0) {
          return null;
        }

        return (
          <Card
            key={register.id}
            size="small"
            title={
              <Flex align="center" justify="space-between">
                <Space size="small" wrap>
                  <Typography.Text strong>{register.label || reviewRegisterLabel(index)}</Typography.Text>
                  <RegisterProgress fields={registerFields} photos={photos} />
                </Space>
                {canRemove ? (
                  <Popconfirm
                    okButtonProps={{ danger: true }}
                    okText={reviewText('register.remove', 'Remove')}
                    onConfirm={() => void onRemoveRegister(register.id)}
                    title={reviewText('register.removeConfirm', 'Remove this register?')}
                  >
                    <Button danger icon={<DeleteOutlined />} size="small" type="text">
                      {reviewText('register.remove', 'Remove')}
                    </Button>
                  </Popconfirm>
                ) : null}
              </Flex>
            }
          >
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <PhotoReviewGallery photos={photos} />
              <Flex justify="flex-start">
                <RegisterPhotoCaptureButton
                  onCapture={file => onCaptureRegisterPhoto(register.id, file)}
                  registerLabel={register.label || reviewRegisterLabel(index)}
                />
              </Flex>
              <ReviewFieldList
                fields={registerFields}
                getLabel={field => formatRegisterFieldLabel(field)}
                onApproveField={onApproveField}
                onOpenField={onOpenField}
              />
            </Space>
          </Card>
        );
      })}
      <Flex justify="flex-start">
        <Button icon={<PlusOutlined />} onClick={() => void onAddRegister()}>
          {reviewText('register.add', 'Add register')}
        </Button>
      </Flex>
    </Space>
  );
}
